/**
 * Main App component with routing
 * =============================================================================
 * Now includes Firebase Authentication with protected routes
 */
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { JobWatcherProvider } from './contexts/JobWatcherContext';
import JobToasts from './components/JobToasts';
import { ImpersonationProvider } from './contexts/ImpersonationContext';
import ImpersonationBanner from './components/ImpersonationBanner';
import LoginPage from './pages/LoginPage';
import FinishSignInPage from './pages/FinishSignInPage';
import SnapshotPage from './pages/SnapshotPage';
import ExportJobPage from './pages/ExportJobPage';
import AdminPage from './pages/AdminPage';
import UnsubscribePage from './pages/UnsubscribePage';
import ProtectedRoute from './components/ProtectedRoute';
import FeedbackButton from './components/FeedbackButton';
import ShellRoot from './shell/ShellRoot';
import { SHELL_ROUTES } from './shell/shellRoutes';

function App() {
  return (
    <AuthProvider>
      <ImpersonationProvider>
        <Router>
          {/* Investigator runs are polled from HERE, above the outlet.
              Inside UnifiedChatPanel the poll died on every navigation, so a
              user who launched a run and went to read something else was
              never told it finished. JobToasts is mounted alongside for the
              same reason: it has to be able to appear on any page. */}
          <JobWatcherProvider>
          <JobToasts />
          {/* Admin impersonation banner — mounted above the router outlet
              so it shows on every page whenever an active impersonation
              session exists (renders null otherwise). */}
          <ImpersonationBanner />
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/finishSignIn" element={<FinishSignInPage />} />
            <Route path="/snapshot/:shareToken" element={<SnapshotPage />} />
            <Route path="/unsubscribe/:token" element={<UnsubscribePage />} />

            {/* Out-of-shell protected routes */}
            <Route
              path="/export/:jobId"
              element={
                <ProtectedRoute>
                  <ExportJobPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminPage />
                </ProtectedRoute>
              }
            />

            {/* Everything else the app hosts renders inside the shell (tabs +
                the one chat panel). The outer route only selects the shell:
                each tab matches its own path through its own router, so the
                children render nothing. Workstream pages stay open to every
                same-org user (report-output-completeness Rule 30); the API
                authorizes, and ProtectedRoute keeps unauthenticated visitors
                on /login before any request is issued. */}
            <Route
              element={
                <ProtectedRoute>
                  <ShellRoot />
                </ProtectedRoute>
              }
            >
              {SHELL_ROUTES.map((r) => (
                <Route key={r.path} path={r.path} element={null} />
              ))}
            </Route>

            {/* 404 route */}
            <Route
              path="*"
              element={
                <div style={{ color: 'red', fontSize: '30px', padding: '20px' }}>
                  No route matched.
                </div>
              }
            />
          </Routes>
          </JobWatcherProvider>
          <FeedbackButton />
        </Router>
      </ImpersonationProvider>
    </AuthProvider>
  );
}

export default App;
